import React, { useMemo, useState } from 'react';

// Lightweight, focused drawer for managing excluded rowers per session (and bulk)
const ExcludedRowersManagerDrawer = ({
  open,
  onClose,
  taskId,
  seatCapacityBalance,
  getUserInfo,
  hook,
  initialSelectedSessionIds = [],
}) => {
  const {
    excludedRowersPerSession,
    savingExcludedRowers,
    addExcludedRower,
    removeExcludedRower,
    updateExcludedRowerReason,
    bulkExcludeRower,
    bulkIncludeRower,
  } = hook || {};

  const sessions = Array.isArray(seatCapacityBalance?.session_analysis)
    ? seatCapacityBalance.session_analysis
    : [];

  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());

  React.useEffect(() => {
    if (open) {
      setSelectedSessionIds(new Set(initialSelectedSessionIds || []));
    }
  }, [open, initialSelectedSessionIds]);

  const problematicSessions = useMemo(
    () =>
      sessions.filter(
        (s) => s.issue_type === 'insufficient_rowers' || s.issue_type === 'excess_rowers'
      ),
    [sessions]
  );

  const toggleSelected = (sid) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const allSelected = useMemo(
    () =>
      problematicSessions.length > 0 &&
      problematicSessions.every((s) => selectedSessionIds.has(s.session_id)),
    [problematicSessions, selectedSessionIds]
  );

  const toggleSelectAll = () => {
    setSelectedSessionIds((prev) => {
      if (allSelected) return new Set();
      return new Set(problematicSessions.map((s) => s.session_id));
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-medium text-gray-900">Manage Excluded Rowers</h3>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
            <div className="mb-1 font-medium text-gray-900">Seat capacity explained</div>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Seats needed = sum of capacities of all experimental crews that are not excluded for
                this session.
              </li>
              <li>
                Available rowers = members from those crews who are available on the session date,
                plus any who are assumed available for the day, or made available via session
                overrides. Rowers explicitly ignored or excluded are not counted, and members with
                no availability data are treated as unavailable unless assumed/overridden.
              </li>
              <li>
                Balance = available rowers − seats needed. Negative → not enough rowers; positive →
                more rowers than seats.
              </li>
              <li>
                Changing exclusions here will trigger background revalidation; updated counts appear
                in the Seat Capacity card.
              </li>
            </ul>
          </div>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">Select affected sessions</div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="rounded"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              <span>Select all</span>
            </label>
          </div>

          <div className="mb-4 max-h-40 overflow-auto rounded border">
            {problematicSessions.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">No sessions with balance issues.</div>
            ) : (
              <ul className="divide-y">
                {problematicSessions.map((s) => (
                  <li key={s.session_id} className="flex items-center justify-between p-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selectedSessionIds.has(s.session_id)}
                        onChange={() => toggleSelected(s.session_id)}
                      />
                      <span className="font-medium">
                        {s.session_title || `Session ${s.session_id}`}
                      </span>
                    </label>
                    <span
                      className={`text-xs ${s.balance_diff < 0 ? 'text-red-600' : s.balance_diff > 0 ? 'text-yellow-600' : 'text-green-600'}`}
                    >
                      {s.balance_diff > 0 ? '+' : ''}
                      {s.balance_diff}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Bulk actions */}
          <div className="mb-4 rounded border p-3">
            <div className="mb-2 text-sm font-medium text-gray-900">Bulk actions</div>
            {selectedSessionIds.size === 0 ? (
              <div className="text-xs text-gray-500">
                Select one or more sessions to enable bulk actions.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-gray-700">
                  Toggle a rower across selected sessions:
                </div>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // collect unique rowers from selected sessions (available + excluded)
                    const setIds = new Set();
                    sessions
                      .filter((s) => selectedSessionIds.has(s.session_id))
                      .forEach((s) => {
                        (s.available_rower_ids || []).forEach((id) => setIds.add(id));
                        (s.excluded_rower_ids || []).forEach((id) => setIds.add(id));
                      });
                    const ids = Array.from(setIds);
                    return ids.map((uid) => {
                      const info = getUserInfo ? getUserInfo(uid) : null;
                      const name = info ? info.name : `User ${uid}`;
                      const isExcludedInAny = Array.from(selectedSessionIds).some((sid) =>
                        (excludedRowersPerSession[sid] || []).some((ex) => ex.user_id === uid)
                      );
                      return (
                        <button
                          key={uid}
                          onClick={() =>
                            isExcludedInAny
                              ? bulkIncludeRower(Array.from(selectedSessionIds), uid)
                              : bulkExcludeRower(Array.from(selectedSessionIds), uid)
                          }
                          className={`rounded px-2 py-1 text-xs ${
                            isExcludedInAny
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                          title={`${isExcludedInAny ? 'Include' : 'Exclude'} across ${selectedSessionIds.size} session(s)`}
                        >
                          {isExcludedInAny ? 'Include' : 'Exclude'} {name}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Per-session editor */}
          <div className="space-y-3">
            {sessions
              .filter((s) => selectedSessionIds.size === 0 || selectedSessionIds.has(s.session_id))
              .map((s) => {
                const available = s.available_rower_ids || [];
                const current = excludedRowersPerSession[s.session_id] || [];
                return (
                  <div key={s.session_id} className="rounded border p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">
                        {s.session_title || `Session ${s.session_id}`}
                      </div>
                      <div className="text-xs text-gray-600">
                        {new Date(s.session_date).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-700">
                          Available rowers
                        </div>
                        <div className="space-y-1">
                          {available.map((uid) => {
                            const info = getUserInfo ? getUserInfo(uid) : null;
                            const name = info ? info.name : `User ${uid}`;
                            const isExcluded = current.some((ex) => ex.user_id === uid);
                            return (
                              <div key={uid} className="flex items-center justify-between text-xs">
                                <span
                                  className={
                                    isExcluded ? 'text-gray-400 line-through' : 'text-gray-700'
                                  }
                                >
                                  {name}
                                </span>
                                <button
                                  onClick={() =>
                                    isExcluded
                                      ? removeExcludedRower(s.session_id, uid)
                                      : addExcludedRower(s.session_id, uid)
                                  }
                                  className={`rounded px-2 py-1 ${isExcluded ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                >
                                  {isExcluded ? 'Include' : 'Exclude'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-700">
                          Currently excluded
                        </div>
                        <div className="space-y-1">
                          {current.length === 0 && (
                            <div className="text-xs text-gray-500">None</div>
                          )}
                          {current.map((ex) => {
                            const info = getUserInfo ? getUserInfo(ex.user_id) : null;
                            const name = info ? info.name : `User ${ex.user_id}`;
                            return (
                              <div
                                key={ex.user_id}
                                className="flex items-start justify-between gap-2 rounded bg-red-50 p-2 text-xs"
                              >
                                <div className="flex-1">
                                  <div className="font-medium text-red-800">{name}</div>
                                  <input
                                    type="text"
                                    className="mt-1 w-full rounded border px-2 py-1"
                                    value={ex.reason || ''}
                                    onChange={(e) =>
                                      updateExcludedRowerReason(
                                        s.session_id,
                                        ex.user_id,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Reason for exclusion..."
                                  />
                                </div>
                                <button
                                  onClick={() => removeExcludedRower(s.session_id, ex.user_id)}
                                  className="rounded bg-red-100 px-2 py-1 text-red-700 hover:bg-red-200"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-gray-600">
                      Seats: {s.total_seats_needed} • Available: {s.available_rowers_count} •
                      Excluded: {current.length}
                    </div>
                  </div>
                );
              })}
          </div>

          {savingExcludedRowers && (
            <div className="mt-3 text-xs text-blue-600">Saving changes…</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcludedRowersManagerDrawer;
